import { useEffect, useRef, useState } from 'react';

/**
 * Keeps the active sidebar entry in view and reports whether more entries sit
 * below the fold. On laptop heights the footer (upgrade card + user) leaves
 * little room, and Einstellungen / Abrechnung otherwise hid without any hint
 * (DEV-535).
 *
 * The active entry is revealed when it changes or when the visible area
 * shrinks (the footer card mounts after entitlements load); plain user
 * scrolling is left alone.
 */
export function useSidebarScrollAffordance<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let revealedActive: Element | null = null;
    let revealedHeight = 0;

    const updateHint = () =>
      setHasMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 12);

    const update = () => {
      updateHint();
      const active = el.querySelector('[data-active="true"]');
      if (
        !active ||
        (active === revealedActive && el.clientHeight >= revealedHeight)
      ) {
        return;
      }
      revealedActive = active;
      revealedHeight = el.clientHeight;
      active.scrollIntoView({ block: 'nearest' });
    };

    update();
    el.addEventListener('scroll', updateHint, { passive: true });
    // Collapsibles change the content height without a scroll event, and nav
    // groups mount after permissions load.
    const resizeObserver = new ResizeObserver(update);
    const observeChildren = () => {
      resizeObserver.observe(el);
      for (const child of Array.from(el.children)) {
        resizeObserver.observe(child);
      }
    };
    observeChildren();
    const mutationObserver = new MutationObserver(() => {
      observeChildren();
      update();
    });
    mutationObserver.observe(el, {
      childList: true,
      subtree: true,
      attributeFilter: ['data-active'],
    });
    return () => {
      el.removeEventListener('scroll', updateHint);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return { ref, hasMoreBelow };
}
